import { useEffect, useMemo, useState } from "react";
import { createTheme, MantineProvider } from "@mantine/core";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { QRCodeCanvas } from "qrcode.react";
import { DatePicker } from "@mantine/dates";
import * as yup from "yup";
import apiService from "../../../../service/apiService";
import { useCookie } from "../../../../hooks/useCookie";
import { useMutation, useQuery } from "react-query";
import {
  createZoneAwareDateTime,
  isNotEmpty,
  minArraySize,
} from "../../../../helpers/general";
import FormInput from "../../../../components/ui/FormInput";
import {
  Control,
  Controller,
  useForm,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import { yupResolver } from "@hookform/resolvers/yup";
import "react-phone-number-input/style.css";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { OpeningHoursModel } from "../../../../helpers/openingHours";
import { CModal, CSpinner } from "@coreui/react";
import { format as dateFnsFormat } from "date-fns";
import { fr as dateFnsFrLocale } from "date-fns/locale";
import { general } from "../../../../locales/general";
import { useToast } from "../../../../hooks/useToast";
// TODO: Update email text to include step for downloading the app (like we have previously in event ticket email) 'Telechargez l'application Venez pour plus d'activités et offres'

interface MinimalSpecialOfferWithVariants extends MinimalSpecialOffer {
  variantsById: Record<string, SpecialOfferVariant>;
}

type SpecialOfferBookingPayload = {
  specialOfferId: string;
  date: string;
  hour: string;
  personsNumber?: number;
  variants?: {
    variantId: string;
    numberOfReservation: number;
  }[];
  fullName: string;
  email?: string;
  phoneNumber?: string;
  paymentMethod: string;
};

const mantineTheme = createTheme({
  primaryColor: "orange",
});

const bookOfferSchema = yup.object({
  // non variant specific fields
  personsNumber: yup
    .number()
    .nullable()
    .default(1)
    .typeError("")
    .transform((_, val) => (val ? Number(val) : null)),
  // date and time
  date: yup.string().required("Ce champ est obligatoire"),
  hour: yup.string().required("Ce champ est obligatoire"),
  // client details
  fullName: yup.string().required("Ce champ est obligatoire"),
  email: yup.string().email("Ce champ doit être une adresse email"),
  phoneNumber: yup.string(),
  paymentMethod: yup.string().required("Ce champ est obligatoire"),
});

export default function PlaceManagerOfferBooking() {
  const { toastError } = useToast();
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [createdBooking, setCreatedBooking] = useState<Booking | null>(null);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const {
    control: formControl,
    getValues,
    trigger,
    setValue,
    watch,
  } = useForm<yup.InferType<typeof bookOfferSchema>>({
    resolver: yupResolver(bookOfferSchema),
    defaultValues: {
      personsNumber: 1,
    },
  });
  const _formNumberOfPersons = watch("personsNumber");
  const _formDate = watch("date");
  const _formHour = watch("hour");
  const [offerHasVariants, setOfferHasVariants] = useState(false);
  // Mapping of variantId to number of persons
  const [selectedVariants, setSelectedVariants] = useState<
    Record<string, number>
  >({});
  const [selectedStep, setSelectedStep] = useState(1);
  const [currentStep, setCurrentStep] = useState(1);
  const { token } = useCookie("vToken");
  const params: {
    id?: string;
  } = useParams();
  const navigate = useNavigate();
  const [bookingPersonsNumber, setBookingPersonsNumber] = useState<number>(1);
  const [bookingTotalPrice, setBookingTotalPrice] = useState<number>(0);

  const newBookingMutation = useMutation(
    async (data: SpecialOfferBookingPayload) => {
      return await apiService.MakePostRequest(
        `booking/place-manager/special-offer`,
        data,
        token,
        false,
      );
    },
  );

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
    setSelectedStep(currentStep + 1);
  };

  const editStep = (step: number) => {
    setSelectedStep(step);
    setCurrentStep(step);
  };

  const {
    data: offerData,
    isLoading: isLoadingOffer,
    isError: isErrorOffer,
  } = useQuery<any, any, MinimalSpecialOfferWithVariants, any>(
    ["getSingleOffer", params?.id],
    async () => {
      const result =
        await apiService.MakeGetRequest<MinimalSpecialOfferWithVariants>(
          `specials/place-manager/${params.id}`,
          token,
        );
      // format special offer variants for easy access
      result.variantsById = {};
      result?.variants?.forEach((variant) => {
        result.variantsById[variant.id] = variant;
      });
      return result;
    },
    {
      refetchInterval: false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  );

  const downloadBookingPdf = async () => {
    try {
      setIsDownloadingPdf(true);
      const response = await fetch(
        `${apiService.baseUrl}booking/${createdBooking?.id}/pdf`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to download the PDF");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `booking_${createdBooking?.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      toastError(general.fr.message.operationFailed);
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  useEffect(() => {
    if (isLoadingOffer || isErrorOffer) {
      return;
    }
    if (offerHasVariants === true) {
      if (Object.keys(selectedVariants).length === 0) {
        setBookingTotalPrice(0);
      } else {
        let numberOfPersons = 0;
        let totalPrice = 0;
        for (const variantId in selectedVariants) {
          const variant = offerData!.variantsById[variantId];
          numberOfPersons += selectedVariants[variantId];
          totalPrice += Number(variant.price) * selectedVariants[variantId];
        }
        setBookingTotalPrice(totalPrice);
        setBookingPersonsNumber(numberOfPersons);
      }
    } else {
      const numberOfPersons = _formNumberOfPersons as number;
      setBookingTotalPrice(numberOfPersons * offerData!.price);
      setBookingPersonsNumber(numberOfPersons);
    }
  }, [
    isLoadingOffer,
    isErrorOffer,
    _formNumberOfPersons,
    selectedVariants,
    offerData?.price,
    setBookingTotalPrice,
    offerHasVariants,
    offerData,
  ]);

  useEffect(() => {
    if (minArraySize(offerData?.variants, 1)) {
      setOfferHasVariants(true);
    } else {
      setOfferHasVariants(false);
    }
    setSelectedStep(1);
    setCurrentStep(1);
  }, [offerData]);

  useEffect(() => {
    const unloadCallback = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => window.removeEventListener("beforeunload", unloadCallback);
  }, []);

  useEffect(() => {
    if (isEmpty(params?.id)) {
      navigate("/place-manager/activities");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id]);

  const submitBookingInfo = async () => {
    setCreatedBooking(null);
    try {
      const data: SpecialOfferBookingPayload = {
        specialOfferId: params.id!,
        fullName: getValues("fullName"),
        email: getValues("email"),
        phoneNumber: getValues("phoneNumber"),
        date: getValues("date"),
        hour: getValues("hour"),
        paymentMethod: getValues("paymentMethod"),
      };
      if (offerHasVariants === true) {
        data.variants = Object.keys(selectedVariants).reduce<
          {
            variantId: string;
            numberOfReservation: number;
          }[]
        >((acc, key) => {
          const variantId = key;
          const numberOfReservation = selectedVariants[variantId];
          return [
            ...acc,
            {
              variantId,
              numberOfReservation,
            },
          ];
        }, []);
      } else {
        data.personsNumber = getValues("personsNumber") as number;
      }
      data.date = createZoneAwareDateTime(new Date(data.date));
      data.email = isEmpty(data.email) ? undefined : data.email;
      data.phoneNumber = isEmpty(data.phoneNumber)
        ? undefined
        : data.phoneNumber;
      // const newBooking = await newBookingMutation.mutateAsync(data, {
      //   onSuccess: (data: Booking) => {
      //     setSuccessModalVisible(true);
      //     setCreatedBooking(data);
      //   },
      //   onError: () => {
      //     toastError(general.fr.message.operationFailed);
      //   },
      // });
    } catch (error) {
      // do nothing
    }
  };

  if (isLoadingOffer) {
    return <div>Chargement...</div>;
  }

  if (isErrorOffer) {
    return <div>Erreur</div>;
  }

  return (
    <MantineProvider theme={mantineTheme}>
      <div
        className="d-flex"
        style={{
          gap: "1.5rem",
          margin: "0 auto",
          maxWidth: "fit-content",
        }}
      >
        {/* booking steps */}
        <div
          className="d-flex flex-col gap-4"
          style={{
            minWidth: "670px",
            width: "670px",
            maxWidth: "670px",
          }}
        >
          {offerHasVariants === true ? (
            <VariantSelectionStep
              index={1}
              stepSelected={selectedStep === 1}
              currentStep={currentStep}
              offer={offerData!}
              nextStep={nextStep}
              editStep={editStep}
              selectedVariants={selectedVariants}
              setSelectedVariants={setSelectedVariants}
            />
          ) : (
            <NumberOfPersonsSelectionStep
              index={1}
              stepSelected={selectedStep === 1}
              currentStep={currentStep}
              offer={offerData!}
              nextStep={nextStep}
              editStep={editStep}
              control={formControl}
              watchValue={watch}
            />
          )}
          <DateTimeSelectionStep
            index={2}
            stepSelected={selectedStep === 2}
            currentStep={currentStep}
            offer={offerData!}
            nextStep={nextStep}
            editStep={editStep}
            control={formControl}
            setValue={setValue}
            watchValue={watch}
          />
          <ContactDetailsStep
            index={3}
            stepSelected={selectedStep === 3}
            currentStep={currentStep}
            offer={offerData!}
            nextStep={nextStep}
            editStep={editStep}
            control={formControl}
            validate={trigger}
            getValues={getValues}
          />
          <PaymentStep
            index={4}
            stepSelected={selectedStep === 4}
            currentStep={currentStep}
            offer={offerData!}
            nextStep={submitBookingInfo}
            totalPrice={bookingTotalPrice}
            control={formControl}
            validate={trigger}
            isLoading={newBookingMutation.isLoading}
          />
        </div>
        {/* booking summary and total price */}
        <div
          style={{
            backgroundColor: "white",
            border: "2px solid #e5e7eb",
            borderRadius: "10px",
            padding: "10px",
            minWidth: "300px",
            width: "300px",
            maxWidth: "300px",
            position: "sticky",
            alignSelf: "flex-start",
            height: "fit-content",
          }}
        >
          {/* image */}
          <div
            className="mb-3"
            style={{
              width: "100%",
              height: "180px",
              backgroundColor: "#e5e7eb",
              borderRadius: "5px",
              backgroundImage: `url('${offerData?.carouselPicture}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          ></div>
          {/* provider of the offer */}
          {isNotEmpty(offerData?.offerProvider) ? (
            <p className="text-gray-700 text-sm">
              Fournie par: {offerData?.offerProvider}
            </p>
          ) : null}
          {/* offer title */}
          <h3 className="font-semibold text-md mb-2 text-gray-800">
            {offerData?.title}
          </h3>
          <HorizontalDivider />
          {/* date + time + persons number */}
          <div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-gray-600 text-sm">Date</span>
              <span className="text-gray-600 text-sm">
                {isNotEmpty(_formDate)
                  ? dateFnsFormat(new Date(_formDate), "EEEE, MMMM d, yyyy", {
                      locale: dateFnsFrLocale,
                    })
                  : "-"}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-gray-600 text-sm">Heure</span>
              <span className="text-gray-600 text-sm">
                {isNotEmpty(_formHour) ? `${_formHour} H` : "-"}
              </span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <span className="text-gray-600 text-sm">Nombre de personnes</span>
              <span className="text-gray-600 text-sm">
                {bookingPersonsNumber}{" "}
                {bookingPersonsNumber > 1 ? "personnes" : "personne"}
              </span>
            </div>
          </div>
          <HorizontalDivider />
          {/* price */}
          <div className="d-flex justify-content-between mb-1">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-lg font-semibold">
              MAD {bookingTotalPrice}
            </span>
          </div>
        </div>
      </div>
      <CModal
        alignment="center"
        visible={successModalVisible}
        onClose={() => setSuccessModalVisible(false)}
      >
        <div className="py-3 px-4 d-flex flex-col align-items-center">
          <div className="booking-modal-success-icon">
            <svg viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          {/* title */}
          <h4>Réservation effectuée</h4>
          <div className="text-md text-gray">
            Votre réservation a été confirmée avec succès
          </div>
          {/* qr code */}
          <div
            className="w-full mt-3"
            style={{
              background: "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)",
              borderRadius: "16px",
              padding: "32px",
              display: "flex",
              justifyContent: "center",
              border: "2px solid #e2e8f0",
            }}
          >
            <QRCodeCanvas
              size={250}
              value={createdBooking?.reference ?? ""}
              style={{
                height: "200px",
                width: "200px",
              }}
            />
          </div>
          {/* download pdf button */}
          {isDownloadingPdf ? (
            <div
              className="w-full mt-3 d-flex justify-content-center align-items-center"
              style={{
                backgroundColor: "#ff7722b0",
                borderRadius: "5px",
                height: "45px",
                aspectRatio: "1/1",
                color: "white",
                border: "none",
                outline: "none",
                padding: "0.5rem 1rem",
              }}
            >
              <CSpinner size="sm" color="#ffffff" />
            </div>
          ) : (
            <button
              className="btn btn-lg w-100 mt-3 bg-gradient-primary text-white"
              style={{
                fontSize: "18px",
              }}
              onClick={downloadBookingPdf}
            >
              Télécharger le PDF
            </button>
          )}
          <Link
            to="/place-manager/activities/"
            replace={true}
            className="btn btn-primary btn-lg w-100 mt-3 text-blue-primary"
            style={{
              backgroundColor: "transparent",
              fontSize: "18px",
            }}
          >
            Retourner à l'accueil
          </Link>
          <div className="text-sm text-gray mt-3 text-center">
            Présentez ce QR code à votre arrivée ou téléchargez le PDF pour
            conserver votre réservation.
          </div>
        </div>
      </CModal>
    </MantineProvider>
  );
}

// * Variant Selection Step
function VariantSelectionStep({
  index,
  stepSelected,
  currentStep,
  offer,
  nextStep,
  editStep,
  selectedVariants,
  setSelectedVariants,
}: {
  index: number;
  stepSelected: boolean;
  currentStep: number;
  offer: MinimalSpecialOfferWithVariants;
  nextStep: () => void;
  editStep: (step: number) => void;
  selectedVariants: Record<string, number>;
  setSelectedVariants: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
}) {
  const [showError, setShowError] = useState(false);

  const moveToNextStep = () => {
    // validate that we have selected at least one variant
    if (Object.keys(selectedVariants).length === 0) {
      setShowError(true);
      return;
    }
    setShowError(false);
    nextStep();
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "2px solid #e5e7eb",
        borderRadius: "10px",
        padding: "15px",
        paddingTop: "20px",
        paddingBottom: "20px",
        opacity: !stepSelected && index > currentStep ? "50%" : "100%",
      }}
    >
      {/* step header */}
      <div className="d-flex items-center gap-2">
        <div
          className="d-flex items-center justify-content-center"
          style={{
            backgroundColor:
              !stepSelected && index > currentStep ? "#E5E7EB" : "#FF7722",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            aspectRatio: "1/1",
          }}
        >
          <span className="font-semibold text-md text-white">{index}</span>
        </div>
        <h3 className="font-semibold text-lg text-gray-800 mb-0">
          Sélection des variantes
        </h3>
        <div className="flex"></div>
        {currentStep > index && (
          <button
            type="button"
            onClick={() => editStep(index)}
            className="reset-button text-blue-primary text-decoration-underline"
          >
            Modifier
          </button>
        )}
      </div>
      {/* summary view */}
      <div
        className="mt-4"
        style={{
          display: !stepSelected && currentStep > index ? "block" : "none",
        }}
      >
        {Object.keys(selectedVariants).map((variantId) => {
          const variant = offer!.variantsById[variantId];
          const numberOfPersons = selectedVariants[variantId];
          return (
            <div className="d-flex gap-2">
              {/* number of persons */}
              <div className="text-bold">
                {numberOfPersons}{" "}
                {numberOfPersons > 1 ? "personnes" : "personne"}
              </div>
              <div className="text-bold">-</div>
              {/* total price */}
              <div className="text-bold">
                MAD {Number(variant.price) * numberOfPersons}
              </div>
              <div className="text-bold">-</div>
              {/* variant name */}
              <div className="text-bold">{variant.description}</div>
            </div>
          );
        })}
      </div>
      {/* step body */}
      <div
        className="mt-4"
        style={{
          display: stepSelected ? "block" : "none",
        }}
      >
        <div className="text-sm mb-4">
          {/* general info */}
          Veuillez sélectionner les variantes souhaitées ainsi que le nombre de
          personnes pour chaque variante avant de continuer.
        </div>
        <div
          className="p-3 mb-4 "
          style={{
            backgroundColor: "#FEE8EB",
            borderRadius: "10px",
            display: showError ? "block" : "none",
          }}
        >
          <span
            className="text-md"
            style={{
              color: "#F70503",
            }}
          >
            Veuillez sélectionner au moins une variante
          </span>
        </div>
        {/* variants list */}
        <div className="d-flex flex-col gap-3">
          {offer?.variants.map((variant) => {
            return (
              <OfferVariantItem
                key={variant.id}
                variant={variant}
                selectedVariants={selectedVariants}
                setSelectedVariants={setSelectedVariants}
              />
            );
          })}
        </div>
        {/* next step button */}
        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            onClick={moveToNextStep}
            style={{
              backgroundColor: "#ff7722",
              borderRadius: "30px",
              height: "40px",
              aspectRatio: "1/1",
              color: "white",
              border: "none",
              outline: "none",
              padding: "0.5rem 1rem",
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function OfferVariantItem({
  variant,
  selectedVariants,
  setSelectedVariants,
}: {
  variant: SpecialOfferVariant;
  selectedVariants: Record<string, number>;
  setSelectedVariants: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
}) {
  const isSelected = selectedVariants[variant.id] > 0;

  const addSelection = () => {
    setSelectedVariants((prev) => {
      const newSelection = { ...prev };
      newSelection[variant.id] = variant.minNumberOfPersons ?? 1;
      return newSelection;
    });
  };

  const incrementPersonsNumber = () => {
    setSelectedVariants((prev) => {
      const newSelection = { ...prev };
      newSelection[variant.id] =
        prev[variant.id] + variant.stepOfNumberOfPersons;
      return newSelection;
    });
  };

  const decrementPersonsNumber = () => {
    setSelectedVariants((prev) => {
      const newSelection = { ...prev };
      newSelection[variant.id] = Math.max(
        prev[variant.id] - variant.stepOfNumberOfPersons,
        variant.minNumberOfPersons ?? 1,
      );
      return newSelection;
    });
  };

  const removeSelection = () => {
    setSelectedVariants((prev) => {
      const newSelection = { ...prev };
      delete newSelection[variant.id];
      return newSelection;
    });
  };

  // const handleSetPersonsNumber = (
  //   event: React.ChangeEvent<HTMLInputElement>,
  // ) => {
  //   const value = parseInt(event.target.value);
  //   setSelectedVariants((prev) => {
  //     const newSelection = { ...prev };
  //     newSelection[variant.id] = value;
  //     return newSelection;
  //   });
  // };

  return (
    <div
      className="d-flex flex-row gap-2"
      style={{
        minWidth: "100%",
        backgroundColor: "#f5f5f5",
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        padding: "0.5rem",
      }}
    >
      {/* icon */}
      <img src={"/specialOffre.png"} alt="variant icon" height={30} />
      {/* variant info */}
      <div className="d-flex flex-col flex items-start">
        <h4 className="text-md mt-1">{variant.description}</h4>
        {/* variant price + add button */}
        <div className="d-flex flex-row justify-content-between items-end mt-2 w-full">
          <div className="text-lg font-semibold">MAD {variant.price}</div>
          {isSelected === true ? (
            <div className="d-flex flex-row gap-1">
              {/* increment button */}
              <button
                type="button"
                onClick={decrementPersonsNumber}
                className="bg-white text-orange-primary rounded-md shadow-md reset-button d-flex align-items-center justify-content-center text-center"
                style={{ width: "45px" }}
              >
                -
              </button>
              {/* number input */}
              <input
                type="number"
                className="bg-white text-gray-900 px-3 py-2 rounded-md shadow-md text-center"
                style={{
                  width: "100px",
                  border: "none",
                  outline: "none",
                }}
                value={selectedVariants[variant.id] ?? 0}
              />
              {/* decrement button */}
              <button
                type="button"
                onClick={incrementPersonsNumber}
                className="bg-white text-orange-primary rounded-md shadow-md reset-button d-flex align-items-center justify-content-center text-center"
                style={{ width: "45px" }}
              >
                +
              </button>
              {/* cancel button */}
              <button
                type="button"
                onClick={removeSelection}
                className="reset-button text-decoration-underline text-orange-primary px-3 py-2"
              >
                Annuler
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={addSelection}
              className="bg-white text-orange-primary px-3 py-2 rounded-md shadow-md reset-button"
            >
              Ajouter +
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// * Number of persons selection step
function NumberOfPersonsSelectionStep({
  index,
  stepSelected,
  currentStep,
  offer,
  nextStep,
  editStep,
  control,
  watchValue,
}: {
  index: number;
  stepSelected: boolean;
  currentStep: number;
  offer: MinimalSpecialOffer;
  nextStep: () => void;
  editStep: (step: number) => void;
  control: Control<yup.InferType<typeof bookOfferSchema>>;
  watchValue: UseFormWatch<yup.InferType<typeof bookOfferSchema>>;
}) {
  const personsNumber = watchValue("personsNumber");

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "2px solid #e5e7eb",
        borderRadius: "10px",
        padding: "15px",
        paddingTop: "20px",
        paddingBottom: "20px",
        opacity: !stepSelected && index > currentStep ? "50%" : "100%",
      }}
    >
      {/* step header */}
      <div className="d-flex items-center gap-2">
        <div
          className="d-flex items-center justify-content-center"
          style={{
            backgroundColor:
              !stepSelected && index > currentStep ? "#E5E7EB" : "#FF7722",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            aspectRatio: "1/1",
          }}
        >
          <span className="font-semibold text-md text-white">{index}</span>
        </div>
        <h3 className="font-semibold text-lg text-gray-800 mb-0">
          Nombre de personnes
        </h3>
        <div className="flex"></div>
        {currentStep > index && (
          <button
            type="button"
            onClick={() => editStep(index)}
            className="reset-button text-blue-primary text-decoration-underline"
          >
            Modifier
          </button>
        )}
      </div>
      {/* summary view */}
      <div
        className="mt-4"
        style={{
          display: !stepSelected && currentStep > index ? "block" : "none",
        }}
      >
        Nombre de personnes : {personsNumber}{" "}
        {(personsNumber ?? 0) > 1 ? "personnes" : "personne"}
      </div>
      {/* step body */}
      <div
        className="mt-4"
        style={{
          display: stepSelected ? "block" : "none",
        }}
      >
        <div className="text-sm mb-4">
          {/* general info */}
          Veuillez sélectionner le nombre de personnes pour la réservation.
        </div>
        <div className="d-flex w-full justify-content-center">
          <Controller
            name="personsNumber"
            control={control}
            render={({ field }) => {
              return (
                <div className="d-flex flex-row gap-1">
                  {/* increment button */}
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = field.value ?? 0;
                      const minimumValue = offer.minNumberOfPersons ?? 1;
                      if (currentValue > minimumValue) {
                        const decrementBy = offer.stepOfNumberOfPersons ?? 1;
                        field.onChange(currentValue - decrementBy);
                      }
                    }}
                    className="bg-white text-orange-primary rounded-md shadow-md reset-button d-flex align-items-center justify-content-center text-center"
                    style={{ width: "45px", border: "1px solid #ff7722" }}
                  >
                    -
                  </button>
                  {/* number input */}
                  <input
                    type="number"
                    className="bg-white text-gray-900 px-3 py-2 rounded-md shadow-md text-center"
                    style={{
                      width: "200px",
                      outline: "none",
                      border: "1px solid #ff7722",
                    }}
                    value={field.value ?? 1}
                  />
                  {/* decrement button */}
                  <button
                    type="button"
                    onClick={() => {
                      const currentValue = field.value ?? 0;
                      const incrementBy = offer.stepOfNumberOfPersons ?? 1;
                      field.onChange(currentValue + incrementBy);
                    }}
                    className="bg-white text-orange-primary rounded-md shadow-md reset-button d-flex align-items-center justify-content-center text-center"
                    style={{ width: "45px", border: "1px solid #ff7722" }}
                  >
                    +
                  </button>
                </div>
              );
            }}
          />
        </div>
        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            onClick={nextStep}
            style={{
              backgroundColor: "#ff7722",
              borderRadius: "30px",
              height: "40px",
              aspectRatio: "1/1",
              color: "white",
              border: "none",
              outline: "none",
              padding: "0.5rem 1rem",
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// * Date and Time Selection Step
function DateTimeSelectionStep({
  index,
  stepSelected,
  currentStep,
  offer,
  nextStep,
  editStep,
  control,
  setValue,
  watchValue,
}: {
  index: number;
  stepSelected: boolean;
  currentStep: number;
  offer: MinimalSpecialOffer;
  nextStep: () => void;
  editStep: (step: number) => void;
  setValue: UseFormSetValue<yup.InferType<typeof bookOfferSchema>>;
  watchValue: UseFormWatch<yup.InferType<typeof bookOfferSchema>>;
  control: Control<yup.InferType<typeof bookOfferSchema>>;
}) {
  const [showHourError, setShowHourError] = useState(false);
  const selectedDate = watchValue("date");
  const selectedHour = watchValue("hour");
  const [dayHours, setDayHours] = useState<string[]>([]);
  const [maxDate, setMaxDate] = useState<Date | undefined>(undefined);
  const placeOpeningHours = useMemo(() => {
    const result = new OpeningHoursModel(
      offer?.openingHours,
      offer?.hoursStep ?? 30,
    );
    return result;
  }, [offer?.hoursStep, offer?.openingHours]);

  useEffect(() => {
    if (isNotEmpty(offer?.date)) {
      const _offerStartDate = new Date(offer.date);
      setValue("date", _offerStartDate.toISOString().split("T")[0]);
    } else {
      setValue("date", new Date().toISOString().split("T")[0]);
    }
    if (!isNotEmpty(offer?.date) || !isNotEmpty(offer?.duration)) {
      // nothing
    } else {
      const startDate = getMinDate();
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + offer?.duration);
      setMaxDate(endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.date, offer?.duration]);

  useEffect(() => {
    // * each time the selected date is changed we calculate the opening hours for that date
    const openingHours = placeOpeningHours.getDayOpeningHours(
      new Date(selectedDate)!,
    );
    setDayHours(openingHours);

    const hourToCheck = selectedHour;
    if (
      hourToCheck &&
      placeOpeningHours.isPlaceOpenAt(new Date(selectedDate), hourToCheck)
    ) {
      setValue("hour", hourToCheck);
    } else {
      setValue("hour", undefined as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeOpeningHours, selectedDate, selectedHour]);

  const getMinDate = () => {
    if (isNotEmpty(offer?.date)) {
      return new Date(offer.date);
    }
    return new Date();
  };

  const moveToNextStep = () => {
    if (isEmpty(selectedDate) || isEmpty(selectedHour)) {
      setShowHourError(true);
      return;
    }
    setShowHourError(false);
    nextStep();
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "2px solid #e5e7eb",
        borderRadius: "10px",
        padding: "15px",
        paddingTop: "20px",
        paddingBottom: "20px",
        opacity: !stepSelected && index > currentStep ? "50%" : "100%",
      }}
    >
      {/* step header */}
      <div className="d-flex items-center gap-2">
        <div
          className="d-flex items-center justify-content-center"
          style={{
            backgroundColor:
              !stepSelected && index > currentStep ? "#E5E7EB" : "#FF7722",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            aspectRatio: "1/1",
          }}
        >
          <span className="font-semibold text-md text-white">{index}</span>
        </div>
        <h3 className="font-semibold text-lg text-gray-800 mb-0">
          La date et l'heure
        </h3>
        <div className="flex"></div>
        {currentStep > index && (
          <button
            type="button"
            onClick={() => editStep(index)}
            className="reset-button text-blue-primary text-decoration-underline"
          >
            Modifier
          </button>
        )}
      </div>
      {/* summary view */}
      <div
        className="mt-4"
        style={{
          display: !stepSelected && currentStep > index ? "block" : "none",
        }}
      >
        <div>Date: {selectedDate}</div>
        <div>Heure: {selectedHour} H</div>
      </div>
      {/* step body */}
      <div
        className="mt-4"
        style={{
          display: stepSelected ? "block" : "none",
        }}
      >
        <div className="text-sm mb-4">
          {/* general info */}
          Veuillez sélectionner les variantes souhaitées ainsi que le nombre de
          personnes pour chaque variante avant de continuer.
        </div>
        <div
          className="p-3 mb-4 "
          style={{
            backgroundColor: "#FEE8EB",
            borderRadius: "10px",
            display: showHourError ? "block" : "none",
          }}
        >
          <span
            className="text-md"
            style={{
              color: "#F70503",
            }}
          >
            Veuillez sélectionner la date et l'heure de la réservation
          </span>
        </div>
        <div className="d-flex justify-content-between">
          <Controller
            name="date"
            control={control}
            render={({ field }) => (
              <DatePicker
                value={field.value}
                minDate={getMinDate()}
                onChange={(date) => {
                  if (isNotEmpty(date)) {
                    setValue("date", date!);
                  }
                }}
                maxDate={maxDate}
              />
            )}
          />
          <div
            style={{
              marginLeft: "45px",
            }}
          >
            <Controller
              name="hour"
              control={control}
              render={({ field }) => {
                return (
                  <div className="row gap-2">
                    {dayHours?.map((hour) => {
                      if (hour === "null") {
                        return null;
                      }
                      const placeOpenAtThisTime =
                        placeOpeningHours.isPlaceOpenAt(
                          new Date(selectedDate),
                          hour,
                        );
                      if (placeOpenAtThisTime) {
                        return (
                          <button
                            className={`col-md-3 rounded-sm p-2 max-xsm:p-1 text-center border border-black text-sm ${
                              field.value === hour
                                ? "border-0 text-white bg-danger"
                                : "bg-white"
                            }`}
                            type="button"
                            key={hour}
                            onClick={() => field.onChange(hour)}
                          >
                            {hour} H
                          </button>
                        );
                      }
                      return (
                        <button
                          className="col-md-3 rounded-sm p-2 text-center text-gray-400 border text-sm cursor-not-allowed"
                          type="button"
                          key={hour}
                          disabled
                        >
                          {hour} H
                        </button>
                      );
                    })}
                  </div>
                );
              }}
            />
          </div>
        </div>
        {/* next step button */}
        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            onClick={moveToNextStep}
            style={{
              backgroundColor: "#ff7722",
              borderRadius: "30px",
              height: "40px",
              aspectRatio: "1/1",
              color: "white",
              border: "none",
              outline: "none",
              padding: "0.5rem 1rem",
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// * Contact Details Step
function ContactDetailsStep({
  index,
  stepSelected,
  currentStep,
  offer,
  nextStep,
  editStep,
  control,
  validate,
  getValues,
}: {
  index: number;
  stepSelected: boolean;
  currentStep: number;
  offer: MinimalSpecialOffer;
  nextStep: () => void;
  editStep: (step: number) => void;
  control: Control<yup.InferType<typeof bookOfferSchema>>;
  validate: UseFormTrigger<yup.InferType<typeof bookOfferSchema>>;
  getValues: UseFormGetValues<yup.InferType<typeof bookOfferSchema>>;
}) {
  const [showEmailPhoneError, setShowEmailPhoneError] = useState(false);
  const nameValue = getValues("fullName");
  const emailValue = getValues("email");
  const phoneNumberValue = getValues("phoneNumber");

  const moveToNextStep = async () => {
    // validate subset of the form only name, email and phone number
    const isValid = await validate(["fullName", "email", "phoneNumber"]);
    const missingEmailAndPhone =
      isEmpty(getValues("email")) && isEmpty(getValues("phoneNumber"));
    // check that we have supplied value for at least email or phone number
    if (isValid === false || missingEmailAndPhone) {
      if (missingEmailAndPhone) {
        setShowEmailPhoneError(true);
      }
      return;
    }
    setShowEmailPhoneError(false);
    nextStep();
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "2px solid #e5e7eb",
        borderRadius: "10px",
        padding: "15px",
        paddingTop: "20px",
        paddingBottom: "20px",
        opacity: !stepSelected && index > currentStep ? "50%" : "100%",
      }}
    >
      {/* step header */}
      <div className="d-flex items-center gap-2">
        <div
          className="d-flex items-center justify-content-center"
          style={{
            backgroundColor:
              !stepSelected && index > currentStep ? "#E5E7EB" : "#FF7722",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            aspectRatio: "1/1",
          }}
        >
          <span className="font-semibold text-md text-white">{index}</span>
        </div>
        <h3 className="font-semibold text-lg text-gray-800 mb-0">
          Informations du client
        </h3>
        <div className="flex"></div>
        {currentStep > index && (
          <button
            type="button"
            onClick={() => editStep(index)}
            className="reset-button text-blue-primary text-decoration-underline"
          >
            Modifier
          </button>
        )}
      </div>
      {/* summary view */}
      <div
        className="mt-4"
        style={{
          display: !stepSelected && currentStep > index ? "block" : "none",
        }}
      >
        {isNotEmpty(nameValue) && <div>Nom: {nameValue}</div>}
        {isNotEmpty(emailValue) && <div>Email: {emailValue}</div>}
        {isNotEmpty(phoneNumberValue) && (
          <div>Téléphone: {phoneNumberValue}</div>
        )}
      </div>
      {/* step body */}
      <div
        className="mt-4"
        style={{
          display: stepSelected ? "block" : "none",
        }}
      >
        <div className="text-sm mb-4">
          {/* general info */}
          Veuillez saisir les informations de contact du client. L&apos;email
          doit être une adresse valide, ou le numéro de téléphone doit être
          associé à WhatsApp, afin de recevoir le QR code de votre réservation.
        </div>
        <div className="row">
          <div className="col-md-12 mb-4">
            <FormInput name="fullName" label="Nom Complet" control={control} />
          </div>
          <div className="col-md-5">
            <FormInput name="email" label="Email" control={control} required />
          </div>
          <div
            className="col-md-2 d-flex align-items-end justify-content-center"
            style={{
              color: "grey",
              marginBottom: "10px",
            }}
          >
            Ou
          </div>
          <div className="col-md-5">
            <label htmlFor="phoneNumber" className="d-block text-sm">
              Numéro de WhatsApp
            </label>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  id="phoneNumber"
                  placeholder="Numéro de WhatsApp"
                  value={field.value}
                  defaultCountry="MA"
                  international
                  required
                  className="phone-input custom-input w-100 mt-1 pe-5"
                  onChange={(value) => field.onChange(value ?? "")}
                />
              )}
            />
          </div>
        </div>
        <div
          className="text-red-500 text-opacity-50 ms-2 text-sm mt-1"
          style={{
            color: "#F70503",
            display: showEmailPhoneError ? "block" : "none",
          }}
        >
          Veuillez fournir au moins un moyen de contact (email ou numéro de
          téléphone).
        </div>
        {/* next step button */}
        <div className="d-flex justify-content-end mt-4">
          <button
            type="button"
            onClick={moveToNextStep}
            style={{
              backgroundColor: "#ff7722",
              borderRadius: "30px",
              height: "40px",
              aspectRatio: "1/1",
              color: "white",
              border: "none",
              outline: "none",
              padding: "0.5rem 1rem",
            }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// * Payment Step
function PaymentStep({
  index,
  stepSelected,
  currentStep,
  offer,
  nextStep,
  totalPrice,
  control,
  validate,
  isLoading,
}: {
  index: number;
  stepSelected: boolean;
  currentStep: number;
  offer: MinimalSpecialOffer;
  nextStep: () => void;
  totalPrice: number;
  control: Control<yup.InferType<typeof bookOfferSchema>>;
  validate: UseFormTrigger<yup.InferType<typeof bookOfferSchema>>;
  isLoading: boolean;
}) {
  const [showError, setShowError] = useState(false);
  const hotelCommission = offer.placeCommission ?? 0;

  const moveToNextStep = async () => {
    const isValid = await validate(["paymentMethod"]);
    if (isValid === false) {
      setShowError(true);
      return;
    }
    setShowError(false);
    nextStep();
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        border: "2px solid #e5e7eb",
        borderRadius: "10px",
        padding: "15px",
        paddingTop: "20px",
        paddingBottom: "20px",
        opacity: !stepSelected && index > currentStep ? "50%" : "100%",
      }}
    >
      {/* step header */}
      <div className="d-flex items-center gap-2">
        <div
          className="d-flex items-center justify-content-center"
          style={{
            backgroundColor:
              !stepSelected && index > currentStep ? "#E5E7EB" : "#FF7722",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            aspectRatio: "1/1",
          }}
        >
          <span className="font-semibold text-md text-white">{index}</span>
        </div>
        <h3 className="font-semibold text-lg text-gray-800 mb-0">Payment</h3>
      </div>
      {/* step body */}
      <div
        className="mt-4"
        style={{
          display: stepSelected ? "block" : "none",
        }}
      >
        <div style={{ marginBottom: "31px" }}>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "20px",
            }}
          >
            Choisissez le mode de paiement
          </div>
          <div
            className="p-3 mb-4 "
            style={{
              backgroundColor: "#FEE8EB",
              borderRadius: "10px",
              display: showError ? "block" : "none",
            }}
          >
            <span
              className="text-md"
              style={{
                color: "#F70503",
              }}
            >
              Veuillez sélectionner le mode de paiement
            </span>
          </div>
          {/* Select payment method (already pay, will pay in place) */}
          <Controller
            name="paymentMethod"
            control={control}
            render={({ field }) => {
              return (
                <div>
                  <label
                    onClick={() => {
                      field.onChange("credit_card");
                      setShowError(false);
                    }}
                    style={{
                      border:
                        field.value === "credit_card"
                          ? "2px solid #ff6b35"
                          : "2px solid #ddd",
                      borderRadius: "8px",
                      padding: "16px",
                      marginBottom: "12px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      transition: "border-color 0.2s",
                    }}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="credit_card"
                      checked={field.value === "credit_card"}
                      onChange={(e) => {
                        field.onChange("credit_card");
                        setShowError(false);
                      }}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: "600", fontSize: "16px" }}>
                        Déjà payé
                      </div>
                      <div style={{ color: "#666", fontSize: "14px" }}>
                        Le client a déjà effectué le paiement
                      </div>
                    </div>
                  </label>

                  <label
                    onClick={() => {
                      field.onChange("in_place");
                      setShowError(false);
                    }}
                    style={{
                      border:
                        field.value === "in_place"
                          ? "2px solid #ff6b35"
                          : "2px solid #ddd",
                      borderRadius: "8px",
                      padding: "16px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      transition: "border-color 0.2s",
                    }}
                  >
                    <input
                      type="radio"
                      name="payment"
                      value="in_place"
                      checked={field.value === "in_place"}
                      onChange={(e) => {
                        field.onChange("in_place");
                        setShowError(false);
                      }}
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: "600", fontSize: "16px" }}>
                        Paiement sur place
                      </div>
                      <div style={{ color: "#666", fontSize: "14px" }}>
                        Le client paiera sur place
                      </div>
                    </div>
                  </label>
                </div>
              );
            }}
          />
        </div>
        {/* Total Price */}
        <div className="d-flex justify-content-between mb-1">
          <span className="text-lg font-semibold">Total</span>
          <span className="text-lg font-semibold">MAD {totalPrice}</span>
        </div>
        <div className="d-flex justify-content-between mb-1">
          <span className="text-md">
            Commission hôtelière ({hotelCommission}%)
          </span>
          <span className="text-md font-semibold">
            MAD {((totalPrice * hotelCommission) / 100).toFixed(2)}
          </span>
        </div>
        {/* book button */}
        {isLoading ? (
          <div
            className="w-full mt-4 d-flex justify-content-center align-items-center"
            style={{
              backgroundColor: "#ff7722b0",
              borderRadius: "5px",
              height: "50px",
              aspectRatio: "1/1",
              color: "white",
              border: "none",
              outline: "none",
              padding: "0.5rem 1rem",
            }}
          >
            <CSpinner size="sm" color="#ffffff" />
          </div>
        ) : (
          <button
            type="button"
            onClick={moveToNextStep}
            className="w-full mt-4"
            style={{
              backgroundColor: "#ff7722",
              borderRadius: "5px",
              height: "50px",
              aspectRatio: "1/1",
              color: "white",
              border: "none",
              outline: "none",
              padding: "0.5rem 1rem",
            }}
          >
            Réserver
          </button>
        )}
      </div>
    </div>
  );
}

function HorizontalDivider() {
  return (
    <div
      style={{
        minWidth: "100%",
        width: "100%",
        height: "1px",
        backgroundColor: "#e5e7eb",
        margin: "10px 0",
      }}
    ></div>
  );
}
